import React, { useState, useEffect, useRef } from 'react';
import {
  X, Building2, Home, Briefcase, BedDouble, Coffee, Layers,
  Search, Phone, Mail, Plus, Trash2, Loader2, Lock,
} from 'lucide-react';
import { KenyaAdministrativeDivisions } from 'kenya-administrative-divisions';
import { useAppTheme } from '../../contexts/ThemeContext';

// ─────────────────────────────────────────────────────────────────────────────
// Property type config
// ─────────────────────────────────────────────────────────────────────────────

export const PROPERTY_TYPES = [
  { value: 'rental',      label: 'Rental Property',         description: 'Residential units rented monthly',       icon: Home,      color: '#3B82F6' },
  { value: 'commercial',  label: 'Commercial Rental',        description: 'Office spaces & commercial units',       icon: Briefcase, color: '#8B5CF6' },
  { value: 'residential', label: 'Residential (For Sale)',   description: 'Homes sold outright or on installments', icon: BedDouble, color: '#10B981' },
  { value: 'ahp',         label: 'Affordable Housing (AHP)', description: 'Social, Affordable & Market tiers',      icon: Layers,    color: '#F59E0B' },
  { value: 'airbnb',      label: 'Airbnb',                   description: 'Short-stay / nightly rentals',           icon: Coffee,    color: '#EF4444' },
];

// ─────────────────────────────────────────────────────────────────────────────
// ApartmentDialog
// ─────────────────────────────────────────────────────────────────────────────

const ApartmentDialog = ({ open, onClose, onSubmit, apartment, loading }) => {
  const { palette, Colors } = useAppTheme();
  const isEditMode = !!apartment;

  // ── State ────────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    propertyType: '',
    name: '', county: '', subCounty: '', ward: '',
    lat: '', lng: '', placeId: '', formattedAddress: '',
    image: '', description: [], contacts: { phones: [], email: '' }
  });
  const [currentDescriptionPoint, setCurrentDescriptionPoint] = useState('');
  const [currentPhone, setCurrentPhone]                       = useState('');
  const [errors, setErrors]                                   = useState({});

  // Kenya administrative divisions
  const [availableSubCounties, setAvailableSubCounties] = useState([]);
  const [availableWards, setAvailableWards]             = useState([]);

  // Google Places search
  const [locationQuery, setLocationQuery]             = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions]         = useState(false);
  const [searchingLocation, setSearchingLocation]     = useState(false);
  const searchTimeoutRef       = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef       = useRef(null);
  const placesNodeRef          = useRef(null); // invisible DOM node required by PlacesService

  const kenyaData = new KenyaAdministrativeDivisions();
  const counties  = kenyaData.getCounties();

  // typeSelected drives the lock mechanic
  const typeSelected = !!formData.propertyType;
  const selectedMeta = PROPERTY_TYPES.find(t => t.value === formData.propertyType);

  // ── Initialise Google Places services ────────────────────────────────────
  useEffect(() => {
    if (window.google && window.google.maps && !autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      placesServiceRef.current       = new window.google.maps.places.PlacesService(placesNodeRef.current);
    }
  }, [open]); // re-check each time dialog opens in case the script loaded late

  // ── Populate form on open ────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      if (apartment) {
        const d = {
          propertyType: apartment.propertyType || '',
          name: apartment.name || '',
          county: apartment.county || '',
          subCounty: apartment.subCounty || '',
          ward: apartment.ward || '',
          lat: apartment.placeData?.lat || '',
          lng: apartment.placeData?.lng || '',
          placeId: apartment.placeData?.place_id || '',
          formattedAddress: apartment.placeData?.formatted_address || '',
          image: apartment.image || '',
          description: apartment.description || [],
          contacts: { phones: apartment.contacts?.phones || [], email: apartment.contacts?.email || '' }
        };
        setFormData(d);
        if (d.formattedAddress) setLocationQuery(d.formattedAddress);
        if (d.county) {
          const cData = kenyaData.getCounties(d.county);
          if (cData?.constituencies) setAvailableSubCounties(cData.constituencies.map(c => c.constituency_name));
          if (d.subCounty) {
            const wards = kenyaData.getWards(d.county, d.subCounty);
            if (Array.isArray(wards)) setAvailableWards(wards);
          }
        }
      } else {
        setFormData({ propertyType: '', name: '', county: '', subCounty: '', ward: '', lat: '', lng: '', placeId: '', formattedAddress: '', image: '', description: [], contacts: { phones: [], email: '' } });
        setLocationQuery(''); setAvailableSubCounties([]); setAvailableWards([]);
      }
      setCurrentPhone(''); setErrors({});
    }
  }, [apartment, open]);

  // ── Google Places helpers ─────────────────────────────────────────────────
  const searchLocation = (query) => {
    if (!query || query.length < 3 || !autocompleteServiceRef.current) {
      setLocationSuggestions([]);
      return;
    }
    setSearchingLocation(true);
    autocompleteServiceRef.current.getPlacePredictions(
      { input: query, componentRestrictions: { country: 'ke' } },
      (predictions, status) => {
        setSearchingLocation(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setLocationSuggestions(predictions);
          setShowSuggestions(true);
        } else {
          setLocationSuggestions([]);
        }
      }
    );
  };

  const handleLocationInputChange = (e) => {
    const value = e.target.value;
    setLocationQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => searchLocation(value), 500);
  };

  const handleLocationSelect = (prediction) => {
    setLocationQuery(prediction.description);
    setShowSuggestions(false);
    setLocationSuggestions([]);

    placesServiceRef.current.getDetails(
      { placeId: prediction.place_id, fields: ['geometry', 'formatted_address', 'place_id'] },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry) {
          setFormData(prev => ({
            ...prev,
            lat:              place.geometry.location.lat(),
            lng:              place.geometry.location.lng(),
            placeId:          place.place_id,
            formattedAddress: place.formatted_address,
          }));
        }
      }
    );
  };

  const clearLocationSelection = () => {
    setFormData(prev => ({ ...prev, lat: '', lng: '', placeId: '', formattedAddress: '' }));
    setLocationQuery(''); setLocationSuggestions([]); setShowSuggestions(false);
  };

  // ── Administrative location helpers ─────────────────────────────────────
  const handleCountyChange = (e) => {
    const v = e.target.value;
    setFormData(prev => ({ ...prev, county: v, subCounty: '', ward: '' }));
    if (v) {
      const cData = kenyaData.getCounties(v);
      setAvailableSubCounties(cData?.constituencies ? cData.constituencies.map(c => c.constituency_name) : []);
      setAvailableWards([]);
    } else { setAvailableSubCounties([]); setAvailableWards([]); }
    if (errors.county) setErrors(prev => ({ ...prev, county: '' }));
  };

  const handleSubCountyChange = (e) => {
    const v = e.target.value;
    setFormData(prev => ({ ...prev, subCounty: v, ward: '' }));
    if (v) { const wards = kenyaData.getWards(formData.county, v); setAvailableWards(Array.isArray(wards) ? wards : []); }
    else setAvailableWards([]);
    if (errors.subCounty) setErrors(prev => ({ ...prev, subCounty: '' }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // ── Phone helpers ────────────────────────────────────────────────────────
  const handleAddPhone = () => {
    if (currentPhone.trim() && formData.contacts.phones.length < 3) {
      if (!/^0\d{9}$/.test(currentPhone.trim())) {
        setErrors(prev => ({ ...prev, currentPhone: 'Invalid phone format (e.g., 0712345678)' }));
        return;
      }
      setFormData({ ...formData, contacts: { ...formData.contacts, phones: [...formData.contacts.phones, currentPhone.trim()] } });
      setCurrentPhone(''); setErrors(prev => ({ ...prev, currentPhone: '' }));
    }
  };

  const handleRemovePhone  = (i) => setFormData({ ...formData, contacts: { ...formData.contacts, phones: formData.contacts.phones.filter((_, idx) => idx !== i) } });
  const handleEmailChange  = (v) => { setFormData({ ...formData, contacts: { ...formData.contacts, email: v } }); if (errors.email) setErrors(prev => ({ ...prev, email: '' })); };

  // ── Description helpers ──────────────────────────────────────────────────
  const handleAddDescPoint    = () => { if (currentDescriptionPoint.trim()) { setFormData({ ...formData, description: [...formData.description, currentDescriptionPoint.trim()] }); setCurrentDescriptionPoint(''); } };
  const handleRemoveDescPoint = (i) => setFormData({ ...formData, description: formData.description.filter((_, idx) => idx !== i) });

  // ── Validation ───────────────────────────────────────────────────────────
  const validateForm = () => {
    const e = {};
    if (!formData.propertyType)       e.propertyType = 'Select a property type';
    if (!formData.name.trim())        e.name         = 'Property name is required';
    if (!formData.county.trim())      e.county       = 'County is required';
    if (!formData.subCounty.trim())   e.subCounty    = 'Sub County is required';
    if (formData.contacts.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contacts.email)) e.email = 'Invalid email format';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Submit / Close ───────────────────────────────────────────────────────
  const handleClose = () => {
    setFormData({ propertyType: '', name: '', county: '', subCounty: '', ward: '', lat: '', lng: '', placeId: '', formattedAddress: '', image: '', description: [], contacts: { phones: [], email: '' } });
    setCurrentDescriptionPoint(''); setCurrentPhone(''); setErrors({});
    setAvailableSubCounties([]); setAvailableWards([]);
    setLocationQuery(''); setLocationSuggestions([]); setShowSuggestions(false);
    onClose();
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    onSubmit(formData);
    handleClose();
  };

  if (!open) return null;

  // ── Theme shortcuts ──────────────────────────────────────────────────────
  const P   = Colors.primary.main;
  const bg  = palette.background;
  const txt = palette.text;
  const bdr = palette.border;

  const inputBase = {
    backgroundColor: bg.tertiary, color: txt.primary,
    border: `1px solid ${bdr}`, borderRadius: '8px',
    padding: '10px 12px', width: '100%', outline: 'none',
    fontSize: '14px', transition: 'border-color 0.15s, box-shadow 0.15s',
  };
  const inputLocked = { ...inputBase, opacity: 0.45, cursor: 'not-allowed', backgroundColor: bg.primary };
  const errStyle    = { color: Colors.status.error, fontSize: '12px', marginTop: '4px' };
  const labelStyle  = { color: txt.secondary, fontSize: '13px', fontWeight: '500', marginBottom: '6px', display: 'block' };

  const onFocus = (e) => { e.target.style.borderColor = P; e.target.style.boxShadow = `0 0 0 2px ${P}25`; };
  const onBlur  = (e) => { e.target.style.borderColor = bdr; e.target.style.boxShadow = 'none'; };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>

      {/* Invisible node required by Google PlacesService */}
      <div ref={placesNodeRef} style={{ display: 'none' }} />

      <div className="rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col"
        style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
          style={{ borderBottom: `1px solid ${bdr}` }}>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${P}15` }}>
              <Building2 className="w-5 h-5" style={{ color: P }} />
            </div>
            <div>
              <h2 className="text-lg font-bold" style={{ color: txt.primary }}>
                {isEditMode ? 'Edit Property' : 'Add New Property'}
              </h2>
              <p className="text-xs mt-0.5" style={{ color: txt.muted }}>
                {isEditMode ? 'Update property details' : 'Fill in the details below'}
              </p>
            </div>
          </div>
          <button onClick={handleClose} type="button"
            className="p-2 rounded-lg transition"
            style={{ color: txt.muted }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = bg.tertiary}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Scrollable body ─────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">

          {/* ── Step 1: Property Type ────────────────────────────────────── */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: typeSelected ? Colors.status.success : P }}>
                {typeSelected ? '✓' : '1'}
              </span>
              <label className="font-semibold text-sm" style={{ color: txt.primary }}>
                Property Type <span style={{ color: Colors.status.error }}>*</span>
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PROPERTY_TYPES.map(type => {
                const Icon     = type.icon;
                const selected = formData.propertyType === type.value;
                return (
                  <button key={type.value} type="button"
                    onClick={() => { setFormData(f => ({ ...f, propertyType: type.value })); setErrors(e => ({ ...e, propertyType: '' })); }}
                    className="flex items-center gap-3 p-3.5 rounded-xl text-left transition border-2"
                    style={{ borderColor: selected ? type.color : bdr, backgroundColor: selected ? `${type.color}12` : bg.tertiary }}
                    onMouseEnter={e => { if (!selected) e.currentTarget.style.borderColor = `${type.color}60`; }}
                    onMouseLeave={e => { if (!selected) e.currentTarget.style.borderColor = bdr; }}>
                    <div className="p-2 rounded-lg flex-shrink-0"
                      style={{ backgroundColor: selected ? `${type.color}20` : `${type.color}10` }}>
                      <Icon className="w-4 h-4" style={{ color: type.color }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: selected ? type.color : txt.primary }}>{type.label}</p>
                      <p className="text-xs truncate" style={{ color: txt.muted }}>{type.description}</p>
                    </div>
                    {selected && (
                      <div className="ml-auto flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: type.color }}>
                        <span className="text-white text-xs">✓</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {errors.propertyType && <p style={errStyle}>{errors.propertyType}</p>}
          </div>

          {/* ── Lock notice ──────────────────────────────────────────────── */}
          {!typeSelected && (
            <div className="flex items-center gap-3 p-4 rounded-xl"
              style={{ backgroundColor: `${P}08`, border: `1px dashed ${P}40` }}>
              <Lock className="w-4 h-4 flex-shrink-0" style={{ color: P }} />
              <p className="text-sm" style={{ color: txt.secondary }}>
                Select a property type above to fill in the remaining details.
              </p>
            </div>
          )}

          {/* ── Rest of form (locked until type selected) ────────────────── */}
          <fieldset disabled={!typeSelected} style={{ opacity: typeSelected ? 1 : 0.45, transition: 'opacity 0.2s' }}>
            <div className="space-y-6">

              {/* Step 2: Property Name */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: P }}>2</span>
                  <label className="font-semibold text-sm" style={{ color: txt.primary }}>
                    Property Details <span style={{ color: Colors.status.error }}>*</span>
                  </label>
                  {selectedMeta && typeSelected && (
                    <span className="ml-auto text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ backgroundColor: `${selectedMeta.color}15`, color: selectedMeta.color }}>
                      {selectedMeta.label}
                    </span>
                  )}
                </div>
                <label style={labelStyle}>Property Name <span style={{ color: Colors.status.error }}>*</span></label>
                <input type="text" name="name" value={formData.name} onChange={handleChange}
                  placeholder="e.g., Sunset Apartments"
                  style={!typeSelected ? inputLocked : errors.name ? { ...inputBase, border: `1px solid ${Colors.status.error}` } : inputBase}
                  onFocus={typeSelected ? onFocus : undefined}
                  onBlur={typeSelected ? onBlur : undefined}
                  disabled={!typeSelected} />
                {errors.name && <p style={errStyle}>{errors.name}</p>}
              </div>

              {/* Step 3: Administrative Location */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: P }}>3</span>
                  <label className="font-semibold text-sm" style={{ color: txt.primary }}>Administrative Location</label>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    ['county',    'County',     handleCountyChange,    counties,             'Select County',     errors.county],
                    ['subCounty', 'Sub County', handleSubCountyChange, availableSubCounties, 'Select Sub County', errors.subCounty],
                  ].map(([name, label, onChange, options, placeholder, err]) => (
                    <div key={name}>
                      <label style={labelStyle}>{label} <span style={{ color: Colors.status.error }}>*</span></label>
                      <select name={name} value={formData[name]} onChange={onChange}
                        disabled={!typeSelected || (name === 'subCounty' && !formData.county)}
                        style={!typeSelected ? { ...inputLocked } : { ...inputBase, border: `1px solid ${err ? Colors.status.error : bdr}` }}
                        onFocus={typeSelected ? onFocus : undefined}
                        onBlur={typeSelected ? onBlur : undefined}>
                        <option value="">{!formData.county && name === 'subCounty' ? 'First select county' : placeholder}</option>
                        {options.map((o, i) => <option key={i} value={o}>{o}</option>)}
                      </select>
                      {err && <p style={errStyle}>{err}</p>}
                    </div>
                  ))}
                  <div>
                    <label style={labelStyle}>Ward <span style={{ color: txt.muted, fontWeight: 400 }}>(optional)</span></label>
                    <select name="ward" value={formData.ward} onChange={handleChange}
                      disabled={!typeSelected || !formData.subCounty}
                      style={!typeSelected ? { ...inputLocked } : { ...inputBase }}
                      onFocus={typeSelected ? onFocus : undefined}
                      onBlur={typeSelected ? onBlur : undefined}>
                      <option value="">{formData.subCounty ? 'Select Ward' : 'First select sub county'}</option>
                      {availableWards.map((w, i) => <option key={i} value={w}>{w}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Step 4: Location Search (Google Places) */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: P }}>4</span>
                  <label className="font-semibold text-sm" style={{ color: txt.primary }}>
                    Location Search <span style={{ color: txt.muted, fontWeight: 400, fontSize: '12px' }}>(optional)</span>
                  </label>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" size={18} style={{ color: txt.muted }} />
                  <input
                    type="text"
                    value={locationQuery}
                    onChange={handleLocationInputChange}
                    onFocus={e => { if (typeSelected) { onFocus(e); locationSuggestions.length > 0 && setShowSuggestions(true); } }}
                    onBlur={e => { if (typeSelected) onBlur(e); }}
                    placeholder="Type to search for a location in Kenya..."
                    disabled={!typeSelected}
                    style={!typeSelected ? { ...inputLocked, paddingLeft: '38px' } : { ...inputBase, paddingLeft: '38px', paddingRight: '38px' }}
                  />
                  {searchingLocation && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin" size={18} style={{ color: txt.muted }} />
                  )}
                  {showSuggestions && locationSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 rounded-lg shadow-lg z-50 max-h-60 overflow-auto"
                      style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
                      {locationSuggestions.map((s) => (
                        <div key={s.place_id} onClick={() => handleLocationSelect(s)}
                          className="p-3 cursor-pointer transition"
                          style={{ borderBottom: `1px solid ${bdr}` }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = bg.tertiary}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <p className="text-sm" style={{ color: txt.primary }}>
                            {s.structured_formatting.main_text}
                          </p>
                          <p className="text-xs mt-0.5" style={{ color: txt.muted }}>
                            {s.structured_formatting.secondary_text}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {formData.formattedAddress && (
                  <div className="mt-3 p-3 rounded-lg flex justify-between items-start"
                    style={{ backgroundColor: `${P}10`, border: `1px solid ${P}30` }}>
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: txt.primary }}>Selected Location</p>
                      <p className="text-sm mt-1" style={{ color: txt.secondary }}>{formData.formattedAddress}</p>
                      {formData.lat && formData.lng && (
                        <p className="text-xs mt-1" style={{ color: txt.muted }}>Coordinates: {formData.lat}, {formData.lng}</p>
                      )}
                    </div>
                    <button type="button" onClick={clearLocationSelection} style={{ color: txt.muted }}>
                      <X size={18} />
                    </button>
                  </div>
                )}
              </div>

              {/* Step 5: Contact Information */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: P }}>5</span>
                  <label className="font-semibold text-sm" style={{ color: txt.primary }}>
                    Contact Information <span style={{ color: txt.muted, fontWeight: 400, fontSize: '12px' }}>(optional)</span>
                  </label>
                </div>

                {/* Phone numbers */}
                <div className="mb-3">
                  <label style={labelStyle}>Phone Numbers (Max 3)</label>
                  <div className="flex gap-2">
                    <input type="tel" value={currentPhone}
                      onChange={e => { setCurrentPhone(e.target.value); if (errors.currentPhone) setErrors(prev => ({ ...prev, currentPhone: '' })); }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddPhone(); } }}
                      placeholder="e.g., 0712345678"
                      disabled={!typeSelected || formData.contacts.phones.length >= 3}
                      style={!typeSelected ? { ...inputLocked, flex: 1 } : { ...inputBase, flex: 1, border: `1px solid ${errors.currentPhone ? Colors.status.error : bdr}` }}
                      onFocus={typeSelected ? onFocus : undefined}
                      onBlur={typeSelected ? onBlur : undefined} />
                    <button type="button" onClick={handleAddPhone}
                      disabled={!typeSelected || formData.contacts.phones.length >= 3}
                      className="px-4 py-2 text-white rounded-lg transition flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                      style={{ backgroundColor: P }}>
                      <Plus size={16} /> Add
                    </button>
                  </div>
                  {errors.currentPhone && <p style={errStyle}>{errors.currentPhone}</p>}
                  {formData.contacts.phones.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {formData.contacts.phones.map((phone, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg"
                          style={{ backgroundColor: bg.tertiary, border: `1px solid ${bdr}` }}>
                          <p className="text-sm" style={{ color: txt.primary }}>{phone}</p>
                          <button type="button" onClick={() => handleRemovePhone(i)}
                            style={{ color: Colors.status.error }}
                            onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                            <Trash2 size={15} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label style={labelStyle}>Email Address <span style={{ color: txt.muted, fontWeight: 400 }}>(optional)</span></label>
                  <input type="email" value={formData.contacts.email}
                    onChange={e => handleEmailChange(e.target.value)}
                    placeholder="e.g., info@property.com"
                    disabled={!typeSelected}
                    style={!typeSelected ? inputLocked : { ...inputBase, border: `1px solid ${errors.email ? Colors.status.error : bdr}` }}
                    onFocus={typeSelected ? onFocus : undefined}
                    onBlur={typeSelected ? onBlur : undefined} />
                  {errors.email && <p style={errStyle}>{errors.email}</p>}
                </div>
              </div>

              {/* Step 6: Description */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: P }}>6</span>
                  <label className="font-semibold text-sm" style={{ color: txt.primary }}>
                    Description <span style={{ color: txt.muted, fontWeight: 400, fontSize: '12px' }}>(optional bullet points)</span>
                  </label>
                </div>
                <div className="flex gap-2 mb-2">
                  <input type="text" value={currentDescriptionPoint}
                    onChange={e => setCurrentDescriptionPoint(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (typeSelected) handleAddDescPoint(); } }}
                    placeholder="Add a description point and press Enter or Add"
                    disabled={!typeSelected}
                    style={!typeSelected ? { ...inputLocked, flex: 1 } : { ...inputBase, flex: 1 }}
                    onFocus={typeSelected ? onFocus : undefined}
                    onBlur={typeSelected ? onBlur : undefined} />
                  <button type="button" onClick={handleAddDescPoint} disabled={!typeSelected}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-40 flex-shrink-0"
                    style={{ backgroundColor: `${P}15`, color: P, border: `1px solid ${P}30` }}>
                    Add
                  </button>
                </div>
                {formData.description.length > 0 && (
                  <ul className="space-y-1.5">
                    {formData.description.map((pt, i) => (
                      <li key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
                        style={{ backgroundColor: bg.tertiary, color: txt.secondary, border: `1px solid ${bdr}` }}>
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: P }} />
                        <span className="flex-1">{pt}</span>
                        <button type="button" onClick={() => handleRemoveDescPoint(i)} style={{ color: txt.muted }}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Step 7: Image URL */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-5 h-5 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: P }}>7</span>
                  <label className="font-semibold text-sm" style={{ color: txt.primary }}>
                    Image URL <span style={{ color: txt.muted, fontWeight: 400, fontSize: '12px' }}>(optional)</span>
                  </label>
                </div>
                <input type="url" name="image" value={formData.image} onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                  disabled={!typeSelected}
                  style={!typeSelected ? inputLocked : inputBase}
                  onFocus={typeSelected ? onFocus : undefined}
                  onBlur={typeSelected ? onBlur : undefined} />
              </div>

            </div>
          </fieldset>
        </div>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 flex-shrink-0"
          style={{ borderTop: `1px solid ${bdr}`, backgroundColor: bg.tertiary }}>
          <button type="button" onClick={handleClose} disabled={loading}
            className="px-5 py-2.5 rounded-lg font-medium text-sm transition disabled:opacity-50"
            style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}`, color: txt.primary }}
            onMouseEnter={e => e.currentTarget.style.borderColor = P}
            onMouseLeave={e => e.currentTarget.style.borderColor = bdr}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={loading || !typeSelected}
            className="px-6 py-2.5 text-white rounded-lg font-semibold text-sm transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            style={{ backgroundColor: P }}>
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading
              ? (isEditMode ? 'Updating...' : 'Adding...')
              : (isEditMode ? 'Save Changes' : 'Add Property')}
          </button>
        </div>

      </div>
    </div>
  );
};

export default ApartmentDialog;